import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Drawer, Empty, Form, Input, Popconfirm, Tabs, Typography, message, theme } from "antd";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import InstructionEditor from "../Components/InstructionEditor";
import "../Components/instruction-editor.css";
import "../Components/matter/LabPreview.css";
import { API_BASE_URL } from "../config/serverApiConfig";

const { Title } = Typography;

const LANGS = [
  { key: "ku", label: "کوردی", dir: "rtl", title: "سەردێڕ", body: "ناوەڕۆک", titleName: "titleKu", bodyName: "contentKu", titlePh: "سەردێڕی کوردی", bodyPh: "ناوەڕۆکی کوردی", bodyError: "ناوەڕۆک پێویستە" },
  { key: "ar", label: "عربي", dir: "rtl", title: "العنوان", body: "المحتوى", titleName: "titleAr", bodyName: "contentAr", titlePh: "العنوان بالعربي", bodyPh: "المحتوى العربي", bodyError: "المحتوى مطلوب" },
  { key: "en", label: "English", dir: "ltr", title: "Title", body: "Content", titleName: "titleEn", bodyName: "contentEn", titlePh: "English title", bodyPh: "English content", bodyError: "Content is required" },
];

function hasInstructionBody(value) {
  const html = value || "";
  if (/<img\b|<iframe\b/i.test(html)) return true;
  return html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
}

function plainText(value) {
  return (value || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}

function mediaMarks(item) {
  const html = `${item.contentKu || ""} ${item.contentAr || ""} ${item.contentEn || ""}`;
  return {
    image: /<img\b/i.test(html),
    video: /<iframe\b/i.test(html),
  };
}

function Faq() {
  const { token } = theme.useToken();
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [previewLang, setPreviewLang] = useState("ku");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [record, setRecord] = useState(null);
  const [lang, setLang] = useState("ku");
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleOnClose = () => {
    setOpen(false);
    setEdit(false);
    setRecord(null);
    setLang("ku");
    form.resetFields();
  };

  const handleDelete = (item) => {
    axios
      .delete(`${API_BASE_URL}instructions/text/${item.id}`)
      .then(() => {
        message.success("ڕێنماییەکە سڕایەوە");
        setData((prev) => prev.filter((row) => row.id !== item.id));
        if (openId === item.id) setOpenId(null);
      })
      .catch(() => {
        message.error("هەڵەیەک هەیە لە سڕینەوە");
      });
  };

  const handleOnFinish = (values) => {
    setConfirmLoading(true);
    const request = edit
      ? axios.put(`${API_BASE_URL}instructions/text/${record.id}`, values)
      : axios.post(`${API_BASE_URL}instructions/text`, values);

    request
      .then((res) => {
        if (edit) {
          setData((prev) => prev.map((item) => (item.id === record.id ? res.data : item)));
          message.success("ڕێنماییەکە نوێکرایەوە");
        } else {
          setData((prev) => [res.data, ...prev]);
          message.success("ڕێنماییەکە زیادکرا");
        }
        handleOnClose();
      })
      .catch(() => {
        message.error(edit ? "هەڵەیەک هەیە لە نوێکردنەوە" : "هەڵەیەک هەیە لە زیادکردن");
      })
      .finally(() => setConfirmLoading(false));
  };

  const focusInvalid = (errorFields) => {
    const name = errorFields?.[0]?.name?.[0] || "";
    if (String(name).endsWith("Ar")) setLang("ar");
    else if (String(name).endsWith("En")) setLang("en");
    else setLang("ku");
  };

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}instructions/text`)
      .then((res) => setData(Array.isArray(res.data) ? res.data : []))
      .catch(() => message.error("ڕێنماییەکان نەهاتن"));
  }, []);

  const handleEdit = (item) => {
    setRecord(item);
    setEdit(true);
    setLang("ku");
    setOpen(true);
  };

  useEffect(() => {
    if (!open || !edit || !record) return;
    form.setFieldsValue({
      titleKu: record.titleKu,
      titleAr: record.titleAr,
      titleEn: record.titleEn,
      contentKu: record.contentKu,
      contentAr: record.contentAr,
      contentEn: record.contentEn,
    });
  }, [open, edit, record, form]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((item) =>
      [item.titleKu, item.titleAr, item.titleEn, plainText(item.contentKu), plainText(item.contentAr), plainText(item.contentEn)]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [data, query]);

  const preview = LANGS.find((item) => item.key === previewLang) || LANGS[0];

  return (
    <div
      className="lab-page instruction-page"
      style={{
        "--lab-surface": token.colorBgContainer,
        "--note-accent": token.colorPrimary,
      }}
    >
      <div className="patient-toolbar note-top">
        <div className="note-top-head">
          <div>
            <Title level={3} style={{ color: token.colorPrimary, margin: 0 }}>
              ڕێنمایی بە نووسین
            </Title>
            <p className="note-top-sub">دەق، وێنە و ڤیدیۆ بۆ چارەخواز</p>
          </div>
          <span className="patient-stat-chip is-active note-count-chip">
            <span className="patient-stat-copy">
              <span className="patient-stat-count">{filtered.length}</span>
              <span className="patient-stat-label">ڕێنمایی</span>
            </span>
          </span>
        </div>
        <Input
          allowClear
          size="middle"
          prefix={<SearchOutlined />}
          className="patient-search-field"
          placeholder="گەڕان بە سەردێڕ یان ناوەڕۆک"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="patient-toolbar-end">
          <div className="patient-toolbar-btns">
            <Button type="primary" className="patient-toolbar-btn" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
              زیادکردن
            </Button>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="cliche-empty">
          <Empty description={query.trim() ? "هیچ ڕێنماییەک نەدۆزرایەوە" : "هیچ ڕێنماییەک نییە"} />
        </div>
      ) : (
        <div className="instruction-list">
          {filtered.map((item) => {
            const marks = mediaMarks(item);
            const isOpen = openId === item.id;
            const body = item[preview.bodyName] || "";
            return (
              <article key={item.id} className={`instruction-card${isOpen ? " is-open" : ""}`}>
                <div className="instruction-card-head">
                  <button type="button" className="instruction-card-title" onClick={() => setOpenId(isOpen ? null : item.id)}>
                    <h3>{item.titleKu || item.titleAr || item.titleEn || "بێ سەردێڕ"}</h3>
                    {!isOpen ? <p>{plainText(item.contentKu) || "بێ ناوەڕۆک"}</p> : null}
                  </button>
                  <div className="instruction-card-actions">
                    <Button className="instruction-icon-btn" type="text" icon={<EditOutlined />} onClick={() => handleEdit(item)} />
                    <Popconfirm title="ئایا دڵنیایت لە سڕینەوە؟" okText="بەڵێ" cancelText="نەخێر" onConfirm={() => handleDelete(item)}>
                      <Button className="instruction-icon-btn" danger type="text" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                </div>

                <div className="instruction-badges">
                  {LANGS.map((language) => (
                    <span key={language.key} className="instruction-badge">
                      {language.label}
                    </span>
                  ))}
                  {marks.image ? <span className="instruction-badge">وێنە</span> : null}
                  {marks.video ? <span className="instruction-badge">ڤیدیۆ</span> : null}
                </div>

                {isOpen ? (
                  <div className="instruction-preview">
                    <div className="instruction-lang-pills">
                      {LANGS.map((language) => (
                        <button
                          key={language.key}
                          type="button"
                          className={previewLang === language.key ? "is-on" : ""}
                          onClick={() => setPreviewLang(language.key)}
                        >
                          {language.label}
                        </button>
                      ))}
                    </div>
                    <h4 dir={preview.dir}>{item[preview.titleName] || "—"}</h4>
                    <div className="instruction-html" dir={preview.dir} dangerouslySetInnerHTML={{ __html: body }} />
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      <Drawer
        title={edit ? "دەستکاری ڕێنمایی" : "زیادکردنی ڕێنمایی"}
        placement="bottom"
        height="100%"
        open={open}
        onClose={handleOnClose}
        destroyOnClose
        styles={{
          body: { padding: 16, overflow: "auto", background: "#f4f7f7" },
          footer: { padding: 12, background: "#fffefc" },
        }}
        footer={
          <div className="instruction-drawer-actions">
            <Button className="patient-toolbar-btn" onClick={handleOnClose}>
              پاشگەزبوونەوە
            </Button>
            <Button className="patient-toolbar-btn" type="primary" loading={confirmLoading} onClick={() => form.submit()}>
              {edit ? "نوێکردنەوە" : "زیادکردن"}
            </Button>
          </div>
        }
      >
        <Form className="instruction-form" layout="vertical" form={form} onFinish={handleOnFinish} onFinishFailed={({ errorFields }) => focusInvalid(errorFields)}>
          <section className="instruction-form-card">
            <p className="instruction-hint">سێ زمان پڕ بکەرەوە. چارەخواز تەنها زمانی ئەپەکەی دەبینێت.</p>
            <Tabs
              activeKey={lang}
              onChange={setLang}
              className="instruction-lang-tabs"
              items={LANGS.map((item) => ({ key: item.key, label: item.label }))}
            />
            {LANGS.map((item) => (
              <div key={item.key} className="instruction-lang-pane" hidden={lang !== item.key} dir={item.dir}>
                <Form.Item label={item.title} name={item.titleName} rules={[{ required: true, message: `${item.title} پێویستە` }]}>
                  <Input size="large" className="instruction-input" placeholder={item.titlePh} dir={item.dir} />
                </Form.Item>
                <Form.Item
                  label={item.body}
                  name={item.bodyName}
                  rules={[{ validator: (_, value) => (hasInstructionBody(value) ? Promise.resolve() : Promise.reject(new Error(item.bodyError))) }]}
                >
                  <InstructionEditor dir={item.dir} placeholder={item.bodyPh} />
                </Form.Item>
              </div>
            ))}
          </section>
        </Form>
      </Drawer>
    </div>
  );
}

export default Faq;
